"use client";

import { useRef, useState } from "react";
import { Accordion, AccordionGroup } from "@/components/ui/Accordion";
import { Container } from "@/components/ui/Container";
import { loadRecaptcha } from "@/lib/recaptcha";
import { ContactDialog } from "./ContactDialog";
import { deliverables, faqs, questions, railItems, roles, steps, workflows } from "./content";
import styles from "./fv.module.css";

type Theme = "light" | "dark";

function VerificationDiagram() {
  return (
    <svg
      viewBox="0 0 500 440"
      role="img"
      aria-label="Diagram linking a business requirement to code, verification, and evidence"
    >
      <defs>
        <radialGradient id="fv-glow">
          <stop className={styles.artGlowStart} />
          <stop offset="1" className={styles.artGlowEnd} />
        </radialGradient>
        <linearGradient id="fv-stroke">
          <stop stopColor="#acc3ff" />
          <stop offset="1" stopColor="#4058d4" />
        </linearGradient>
        <pattern id="fv-grid" width="28" height="28" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r=".7" fill="#6b87c0" opacity=".3" />
        </pattern>
      </defs>
      <rect width="500" height="440" fill="url(#fv-grid)" />
      <circle cx="250" cy="210" r="210" fill="url(#fv-glow)" />
      <g fill="none" stroke="#4760b6" strokeWidth=".7">
        <ellipse cx="250" cy="210" rx="175" ry="175" />
        <ellipse cx="250" cy="210" rx="122" ry="122" />
        <ellipse cx="250" cy="210" rx="65" ry="175" transform="rotate(40 250 210)" />
        <ellipse cx="250" cy="210" rx="65" ry="175" transform="rotate(-40 250 210)" />
      </g>
      <g fill="none" stroke="url(#fv-stroke)" strokeWidth="1.4">
        <path d="M250 63V142M110 211H185M315 211H390M250 280V352M150 110L205 165M295 258L350 313" />
      </g>
      <g className={styles.artCore}>
        <rect x="185" y="144" width="130" height="132" rx="4" />
        <path d="M250 168l32 13v30c0 24-32 43-32 43s-32-19-32-43v-30z" className={styles.artShield} />
        <path d="M235 209l10 10 22-24" fill="none" stroke="white" strokeWidth="3" />
      </g>
      <g className={styles.artBoxes}>
        <rect x="172" y="34" width="156" height="41" />
        <rect x="24" y="191" width="121" height="41" />
        <rect x="355" y="191" width="122" height="41" />
        <rect x="166" y="347" width="169" height="41" />
      </g>
      <g className={styles.artText} fontSize="10" textAnchor="middle" letterSpacing="1">
        <text x="250" y="59">BUSINESS REQUIREMENT</text>
        <text x="84" y="216">CODE + CONFIG</text>
        <text x="416" y="216">PROOF ENGINE</text>
        <text x="250" y="372">VERSIONED EVIDENCE</text>
      </g>
      <g fill="#a4c0ff">
        <circle cx="250" cy="112" r="3" />
        <circle cx="164" cy="211" r="3" />
        <circle cx="337" cy="211" r="3" />
        <circle cx="250" cy="316" r="3" />
      </g>
    </svg>
  );
}

/* The mockup starts in light mode on every visit and doesn't remember the choice. */
function ThemeToggle({ theme, onToggle }: { theme: Theme; onToggle: () => void }) {
  const label = theme === "light" ? "Switch to dark mode" : "Switch to light mode";

  return (
    <button
      type="button"
      className={styles.themeToggle}
      aria-label={label}
      aria-pressed={theme === "dark"}
      title={label}
      onClick={onToggle}
    >
      {theme === "light" ? (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M20.5 14A8.8 8.8 0 0 1 10 3.5 8.8 8.8 0 1 0 20.5 14Z" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.4 1.4m11.2 11.2L19 19M5 19l1.4-1.4M17.6 6.4 19 5" />
        </svg>
      )}
    </button>
  );
}

export function FormalVerificationContent() {
  const [theme, setTheme] = useState<Theme>("light");
  const dialogRef = useRef<HTMLDialogElement>(null);

  // reCAPTCHA v3 scores a visitor by watching them, so it needs this head start.
  function openDialog() {
    dialogRef.current?.showModal();
    void loadRecaptcha();
  }

  return (
    <div className={styles.root} data-theme={theme}>
      <div className={styles.hero}>
        <Container padding="none" className={styles.heroInner}>
          <ThemeToggle theme={theme} onToggle={() => setTheme(theme === "light" ? "dark" : "light")} />
          <div>
            <p className={styles.heroEyebrow}>Formal verification &amp; continuous assurance</p>
            <h1 className={styles.heroTitle}>Continuous assurance for systems that keep evolving.</h1>
            <p className={styles.heroLead}>
              Reduce the risk of exploits and unintended behavior. AI-assisted formal verification rechecks agreed
              properties as code, permissions, and dependencies change.
            </p>
            <button type="button" className={styles.button} onClick={openDialog}>
              Discuss your verification needs <span className={styles.arrow} aria-hidden="true">↗</span>
            </button>
          </div>
          <div className={styles.heroArt}>
            <VerificationDiagram />
          </div>
        </Container>
      </div>

      <div className={styles.rail}>
        <Container padding="none" className={styles.railInner}>
          {railItems.map((item) => (
            <div className={styles.railItem} key={item.number}>
              <span className={styles.railNumber}>{item.number}</span>
              <div>
                <strong className={styles.railTitle}>{item.title}</strong>
                <small className={styles.railDescription}>{item.description}</small>
              </div>
            </div>
          ))}
        </Container>
      </div>

      <section className={styles.section}>
        <Container padding="none" className={styles.split}>
          <div>
            <p className={styles.eyebrow}>Beyond the audit</p>
            <h2 className={styles.heading}>Keep verification current.</h2>
            <p className={styles.lede}>An audit assesses a version of your system. Your code keeps changing.</p>
            <p className={styles.lede}>
              Continuous verification rechecks agreed properties as code, configuration, and dependencies change.
            </p>
          </div>
          <div className={styles.ruleList}>
            {questions.map((item) => (
              <div className={styles.ruleRow} key={item.number}>
                <span className={styles.ruleNumber}>{item.number}</span>
                <div>
                  <h3 className={styles.ruleTitle}>{item.question}</h3>
                  <p className={styles.ruleBody}>{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section id="approach" className={`${styles.section} ${styles.approach}`}>
        <Container padding="none">
          <h2 className={`${styles.heading} ${styles.approachHeading}`}>From initial verification to ongoing checks.</h2>
          <div className={styles.processGrid}>
            {steps.map((step) => (
              <article className={styles.step} key={step.label}>
                <p className={styles.stepLabel}>{step.label}</p>
                <h3 className={styles.stepTitle}>{step.title}</h3>
                <p className={styles.stepBody}>{step.description}</p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className={`${styles.section} ${styles.roles}`}>
        <Container padding="none" className={styles.split}>
          <div>
            <h2 className={`${styles.heading} ${styles.rolesHeading}`}>Expert review and automated verification.</h2>
            <p className={styles.rolesLead}>
              Experts help define the specifications and models. Automated verification handles routine checks; experts
              investigate findings and provide scoped reviews.
            </p>
          </div>
          <div>
            {roles.map((role) => (
              <div className={styles.role} key={role.name}>
                <h3 className={styles.roleName}>{role.name}</h3>
                <p className={styles.roleBody}>{role.description}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section id="coverage" className={styles.section}>
        <Container padding="none">
          <p className={styles.eyebrow}>Where to begin</p>
          <h2 className={styles.heading}>Verify your critical workflows.</h2>
          <p className={styles.lede}>Focus on high-impact failures and the rules that prevent them.</p>
          <div className={styles.scopeGrid}>
            {workflows.map((workflow) => (
              <article className={styles.scope} key={workflow.title}>
                <div className={styles.symbol} aria-hidden="true">{workflow.symbol}</div>
                <h3 className={styles.scopeTitle}>{workflow.title}</h3>
                <p className={styles.scopeBody}>{workflow.description}</p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className={`${styles.section} ${styles.deliverables}`}>
        <Container padding="none" className={styles.split}>
          <div>
            <p className={styles.eyebrow}>What your team receives</p>
            <h2 className={styles.heading}>Verification evidence your team can use.</h2>
            <p className={styles.lede}>See what was checked, under which assumptions, and what needs attention.</p>
            <div className={styles.engagement}>
              <h3 className={styles.engagementTitle}>Setup and ongoing verification.</h3>
              <p className={styles.engagementBody}>
                Start with specifications, models, and baseline verification. Continue with automated checks, model
                maintenance, and scoped expert reviews. Agree on coverage and compute budgets upfront.
              </p>
            </div>
          </div>
          <ul className={styles.checklist}>
            {deliverables.map((item) => (
              <li className={styles.checklistItem} key={item.title}>
                <span className={styles.checklistArrow} aria-hidden="true">↗</span>
                <div>
                  {item.title}
                  <small className={styles.checklistDescription}>{item.description}</small>
                </div>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <section id="questions" className={styles.section}>
        <Container padding="none">
          <div className={styles.faq}>
            <p className={styles.eyebrow}>Common questions</p>
            <h2 className={`${styles.heading} ${styles.faqHeading}`}>What continuous assurance means</h2>
            <AccordionGroup className={styles.faqList}>
              {faqs.map((faq) => (
                <Accordion
                  key={faq.question}
                  summary={faq.question}
                  triggerClassName={styles.faqTrigger}
                  panelClassName={styles.faqAnswer}
                >
                  <p>{faq.answer}</p>
                </Accordion>
              ))}
            </AccordionGroup>
          </div>
        </Container>
      </section>

      <section className={styles.cta}>
        <Container padding="none" className={styles.ctaInner}>
          <div>
            <h2 className={styles.ctaHeading}>Get started with formal verification.</h2>
            <p className={styles.ctaLead}>Tell us what you need to verify.</p>
          </div>
          <button type="button" className={`${styles.button} ${styles.buttonLight}`} onClick={openDialog}>
            Get verified <span className={styles.arrow} aria-hidden="true">↗</span>
          </button>
        </Container>
      </section>

      <ContactDialog dialogRef={dialogRef} />
    </div>
  );
}
